// The Access profile and Started by filters and the row of active-filter chips, which only show
// with the analytics_segment_filters_enabled gate. analytics-filter-chips.tsx lays out the
// whole filter bar; use-analytics-filters.ts holds the state behind it.
import { BoltIcon, IdentificationIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { AdminAnalyticsSegmentFilter, type AdminAnalyticsStartedBy } from '~/api/graphql';
import {
  type AnalyticsProfileOption,
  CURRENT_PROFILE_HINT,
  findProfileOption,
  profileOptionLabel,
  profileSelectionLabel,
  profileSelectionOf,
  SEGMENT_FILTER_NAMES,
  STARTED_BY_OPTIONS,
  startedByLabel
} from '~/pages/settings/analytics-segment-filters';
import { AnalyticsFilterPicker } from './analytics-filter-picker';
import {
  type AgencyProfileSelection,
  SEGMENT_FILTER_KEYS,
  type SegmentFilterKey,
  type useAnalyticsFilters
} from './use-analytics-filters';

const ALL_PROFILES_LABEL = 'All profiles';
const NO_SELECTION_KEY = '';
const NO_PROFILE_KEY = 'no-profile';

function profileOptionKey(option: AnalyticsProfileOption): string {
  return option.agencyProfileId ?? NO_PROFILE_KEY;
}

export const ProfilePicker = ({
  options,
  selection,
  onSelect
}: {
  options: readonly AnalyticsProfileOption[];
  selection: AgencyProfileSelection | undefined;
  onSelect: (selection: AgencyProfileSelection | undefined) => void;
}) => {
  const selectedOption = findProfileOption(options, selection);
  return (
    <AnalyticsFilterPicker
      icon={<IdentificationIcon className="size-4" />}
      label={selectedOption ? profileOptionLabel(selectedOption) : ALL_PROFILES_LABEL}
      heading={SEGMENT_FILTER_NAMES[AdminAnalyticsSegmentFilter.AgencyProfile]}
      isActive={selectedOption != null}
      selectedKey={selectedOption ? profileOptionKey(selectedOption) : NO_SELECTION_KEY}
      options={[
        { key: NO_SELECTION_KEY, label: ALL_PROFILES_LABEL, onSelect: () => onSelect(undefined) },
        ...options.map((option) => ({
          key: profileOptionKey(option),
          label: profileOptionLabel(option),
          onSelect: () => onSelect(profileSelectionOf(option))
        }))
      ]}
    />
  );
};

export const StartedByPicker = ({
  startedBy,
  onSelect
}: {
  startedBy: AdminAnalyticsStartedBy | undefined;
  onSelect: (startedBy: AdminAnalyticsStartedBy | undefined) => void;
}) => {
  const filterName = SEGMENT_FILTER_NAMES[AdminAnalyticsSegmentFilter.StartedBy];
  return (
    <AnalyticsFilterPicker
      icon={<BoltIcon className="size-4" />}
      label={startedBy ? `${filterName}: ${startedByLabel(startedBy)}` : filterName}
      heading={filterName}
      isActive={startedBy != null}
      selectedKey={startedBy ?? NO_SELECTION_KEY}
      options={[
        { key: NO_SELECTION_KEY, label: 'Any', onSelect: () => onSelect(undefined) },
        ...STARTED_BY_OPTIONS.map((option) => ({
          key: option.value,
          label: option.label,
          onSelect: () => onSelect(option.value)
        }))
      ]}
    />
  );
};

type ActiveFilterChip = {
  name: string;
  value: string;
  onRemove: () => void;
};

function activeFilterChips(
  filters: ReturnType<typeof useAnalyticsFilters>,
  selectedUserLabel: string | undefined
): ActiveFilterChip[] {
  const { selectedProfileOption, viewState } = filters;
  const chipByKey: Record<
    SegmentFilterKey,
    { filter: AdminAnalyticsSegmentFilter; value: string | undefined; onRemove: () => void }
  > = {
    agencyProfile: {
      filter: AdminAnalyticsSegmentFilter.AgencyProfile,
      value:
        viewState.agencyProfile &&
        profileSelectionLabel(viewState.agencyProfile, selectedProfileOption),
      onRemove: () => filters.setAgencyProfile(undefined)
    },
    agencyUserId: {
      filter: AdminAnalyticsSegmentFilter.User,
      value: selectedUserLabel,
      onRemove: () => filters.setAgencyUserId(undefined)
    },
    startedBy: {
      filter: AdminAnalyticsSegmentFilter.StartedBy,
      value: viewState.startedBy && startedByLabel(viewState.startedBy),
      onRemove: () => filters.setStartedBy(undefined)
    }
  };
  return SEGMENT_FILTER_KEYS.flatMap((key) => {
    const { filter, value, onRemove } = chipByKey[key];
    return value ? [{ name: SEGMENT_FILTER_NAMES[filter], value, onRemove }] : [];
  });
}

/** One removable chip per active filter. Removing a chip moves focus to that filter's picker. */
export const SegmentFilterChips = ({
  filters,
  selectedUserLabel
}: {
  filters: ReturnType<typeof useAnalyticsFilters>;
  selectedUserLabel: string | undefined;
}) => {
  const chips = activeFilterChips(filters, selectedUserLabel);
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((chip) => (
        <Badge key={chip.name} color="sky">
          {chip.name}: {chip.value}
          <button
            type="button"
            onClick={() => {
              document.querySelector<HTMLElement>(`[data-filter-trigger="${chip.name}"]`)?.focus();
              chip.onRemove();
            }}
            aria-label={`Remove the ${chip.name} filter`}
            className="-mr-1 rounded p-0.5 hover:bg-sky-500/20 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
          >
            <XMarkIcon className="size-3.5" />
          </button>
        </Badge>
      ))}
      {filters.viewState.agencyProfile && (
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          {CURRENT_PROFILE_HINT}
        </span>
      )}
    </div>
  );
};
